/**
 * Task Detail Modal Component
 * Full task view with editing, comments, and history
 */

import { useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { ModalErrorBoundary } from '@/components/ErrorBoundary';
import {
  Calendar,
  Clock,
  User,
  FileText,
  Edit,
  Trash2,
  Loader2,
  History,
  MessageSquare,
  Paperclip,
} from 'lucide-react';
import { format } from 'date-fns';
import { useSession } from '@/hooks/useSession';
import { useTask, useDeleteTask, useTaskAuditLogs } from '../hooks/useTasks';
import { TaskStatusBadge } from './TaskStatusBadge';
import { TaskPriorityBadge } from './TaskPriorityBadge';
import { TaskComments } from './TaskComments';
import { TaskAttachments } from './TaskAttachments';
import { TaskFormModal } from './TaskFormModal';
import { formatDate, formatDateTime } from '../utils/dateUtils';
import type { Task, TaskListItem } from '../types';
import type { QuoteOption } from '../api';

interface TaskDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  taskId?: string;
  taskPreview?: TaskListItem;
  quotes?: QuoteOption[];
  assignees?: Array<{ id: string; name: string; email?: string }>;
  canEditTasks?: boolean;
  canDeleteTasks?: boolean;
  canAssignTasks?: boolean;
}

export function TaskDetailModal({
  open,
  onOpenChange,
  taskId,
  taskPreview,
  quotes = [],
  assignees = [],
  canEditTasks = true,
  canDeleteTasks = false,
  canAssignTasks = false,
}: TaskDetailModalProps) {
  const { user: session } = useSession();
  const { data: task, isLoading } = useTask(taskId);
  const { data: auditLogsData } = useTaskAuditLogs(taskId);
  const deleteTask = useDeleteTask();

  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Use task data or preview while loading
  // Note: taskPreview is TaskListItem which is missing some fields from Task
  const displayTask: Task | TaskListItem | undefined = task || taskPreview;
  const displayQuote = displayTask?.quote_id
    ? quotes.find((q) => q.id === displayTask.quote_id)
    : undefined;
  const displayQuoteNumber = displayQuote?.quote_number;

  const handleDelete = async () => {
    if (!taskId) return;
    try {
      await deleteTask.mutateAsync(taskId);
      setShowDeleteConfirm(false);
      onOpenChange(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete task';
      toast.error(message);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          {isLoading && !displayTask ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : displayTask ? (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground font-mono">
                        {displayQuoteNumber ? `#${displayQuoteNumber}` : displayTask.task_number}
                      </span>
                      <TaskStatusBadge status={displayTask.status} />
                      <TaskPriorityBadge priority={displayTask.priority} showLabel />
                    </div>
                    <DialogTitle className="text-xl">{displayTask.title}</DialogTitle>
                  </div>
                  <div className="flex items-center gap-2">
                    {canEditTasks && (
                      <Button variant="outline" size="sm" onClick={() => setShowEditModal(true)}>
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                    )}
                    {canDeleteTasks && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowDeleteConfirm(true)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </DialogHeader>

              <Tabs defaultValue="details" className="mt-4">
                <TabsList>
                  <TabsTrigger value="details">Details</TabsTrigger>
                  <TabsTrigger value="attachments" className="flex items-center gap-1">
                    <Paperclip className="h-4 w-4" />
                    Files
                  </TabsTrigger>
                  <TabsTrigger value="comments" className="flex items-center gap-1">
                    <MessageSquare className="h-4 w-4" />
                    Comments
                    {(task?.comments_count ?? 0) > 0 && (
                      <Badge variant="secondary" className="ml-1">
                        {task?.comments_count}
                      </Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="history" className="flex items-center gap-1">
                    <History className="h-4 w-4" />
                    History
                  </TabsTrigger>
                </TabsList>

                {/* Details Tab */}
                <TabsContent value="details" className="space-y-4 mt-4">
                  {/* Description */}
                  {displayTask.description && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium">Description</h4>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                        {displayTask.description}
                      </p>
                    </div>
                  )}

                  <Separator />

                  {/* Meta Info Grid */}
                  <div className="grid grid-cols-2 gap-4">
                    {/* Quote */}
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <FileText className="h-4 w-4" />
                        <span>Quote</span>
                      </div>
                      <p className="text-sm font-medium">
                        {displayTask.quote_id ? (
                          displayQuoteNumber ? (
                            <>
                              <span className="font-mono">#{displayQuoteNumber}</span>
                              {(displayQuote?.account_name || displayQuote?.project_name) && (
                                <span className="text-muted-foreground">
                                  {' '}
                                  {displayQuote.account_name || displayQuote.project_name}
                                </span>
                              )}
                            </>
                          ) : (
                            displayTask.quote_id
                          )
                        ) : (
                          <span className="text-muted-foreground">No quote</span>
                        )}
                      </p>
                    </div>

                    {/* Assignee */}
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <User className="h-4 w-4" />
                        <span>Assignee</span>
                      </div>
                      <p className="text-sm font-medium">
                        {displayTask.assigned_to_name || (
                          <span className="text-muted-foreground">Unassigned</span>
                        )}
                      </p>
                    </div>

                    {/* Due Date */}
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        <span>Due Date</span>
                      </div>
                      <p className="text-sm font-medium">{formatDate(displayTask.due_date)}</p>
                    </div>

                    {/* Estimated Hours */}
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock className="h-4 w-4" />
                        <span>Time</span>
                      </div>
                      <p className="text-sm font-medium">
                        {displayTask.actual_hours ?? 0}h / {displayTask.estimated_hours ?? '-'}h
                      </p>
                    </div>
                  </div>

                  {/* Tags */}
                  {displayTask.tags.length > 0 && (
                    <>
                      <Separator />
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium">Tags</h4>
                        <div className="flex flex-wrap gap-2">
                          {displayTask.tags.map((tag) => (
                            <Badge key={tag} variant="secondary">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Created/Updated Info */}
                  <Separator />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>
                      Created {formatDateTime(displayTask.created_at)}
                      {'created_by_name' in displayTask && displayTask.created_by_name && ` by ${displayTask.created_by_name}`}
                    </span>
                    {'updated_at' in displayTask && (
                      <span>Updated {formatDateTime(displayTask.updated_at)}</span>
                    )}
                  </div>
                </TabsContent>

                {/* Attachments Tab */}
                <TabsContent value="attachments" className="mt-4">
                  <ModalErrorBoundary>
                    {taskId && <TaskAttachments taskId={taskId} canEditTasks={canEditTasks} />}
                  </ModalErrorBoundary>
                </TabsContent>

                {/* Comments Tab */}
                <TabsContent value="comments" className="mt-4">
                  <ModalErrorBoundary>
                    {taskId && task?.comments && (
                      <TaskComments
                        taskId={taskId}
                        comments={task.comments}
                        currentUserId={session?._id}
                        canEditTasks={canEditTasks}
                      />
                    )}
                  </ModalErrorBoundary>
                </TabsContent>

                {/* History Tab */}
                <TabsContent value="history" className="mt-4">
                  {auditLogsData?.logs && auditLogsData.logs.length > 0 ? (
                    <div className="space-y-3">
                      {auditLogsData.logs.map((log) => (
                        <div key={log.id} className="flex gap-3 text-sm">
                          <div className="flex-shrink-0 w-32 text-muted-foreground">
                            {format(new Date(log.created_at), 'MMM d, h:mm a')}
                          </div>
                          <div>
                            <span className="font-medium">
                              {log.actor_name || 'System'}
                            </span>{' '}
                            <span className="text-muted-foreground">{log.action}</span>
                            {log.changes && Object.keys(log.changes).length > 0 && (
                              <div className="mt-1 text-xs text-muted-foreground">
                                {Object.entries(log.changes).map(([key, value]) => (
                                  <div key={key}>
                                    {key}: {JSON.stringify(value)}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No history available
                    </p>
                  )}
                </TabsContent>
              </Tabs>
            </>
          ) : (
            <div className="py-12 text-center text-muted-foreground">Task not found</div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      {showEditModal && task && (
        <TaskFormModal
          open={showEditModal}
          onOpenChange={setShowEditModal}
          task={task}
          quotes={quotes}
          assignees={assignees}
          canAssignTasks={canAssignTasks}
        />
      )}

      {/* Delete Confirmation */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Task</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete this task? This action cannot be undone.
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteTask.isPending}
            >
              {deleteTask.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
